import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import PageBreadcrumb from '../../../../Components/Common/PageBreadcrumb';
import classnames from 'classnames';
import dayjs from 'dayjs';
import {
  IMPROVEMENT_STATUS_META,
  CLARIFICATION_STATUS_META,
} from '../../utils/improvementClarificationHelpers';
import useImprovementClarificationDetail from '../../hooks/useImprovementClarificationDetail';
import useProcurementDetail from '../../../../shared/offer-domain/procurement/useProcurementDetail';
import { useQuantityDeliveries } from '../../../../Components/QuantityTable';
import {
  getVisibleProcurementClauses,
  useClarificationHasUnread,
  useMarkClarificationRespondsAsRead,
} from '../../../../shared/offer-domain/respond';
import { RESPOND_LIST_PAGE_SIZE } from '../../../../shared/offer-domain/respond/listParams';
import useOfferMitraIdentity from '../../hooks/useOfferMitraIdentity';
import { useSubmittedBidder } from '../../te-bridge';
import Improvement from './Improvement';
import Clarification from './Clarification';
import '../../ImprovementClarification.css';
import { DetailHeader } from '../../../../Components/Common/Detail';
import {
  formatPeriodRange,
  getContractTypeLabel,
} from '../../../TenderOpening/utils/tenderOpeningHelpers';
import StatusBadge, {
  colorToTone,
} from '../../../../Components/Common/StatusBadge';

const DetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(1);
  const [visitedTabs, setVisitedTabs] = useState({ 1: true });
  const [readSynced, setReadSynced] = useState(false);
  const [localClarificationProgress, setLocalClarificationProgress] =
    useState(null);
  const {
    statusImprovement: statusImprovementFromState,
    statusClarification: statusClarificationFromState,
    statusClarificationProgress: statusClarificationProgressFromState,
    procurementMitraId: seedProcurementMitraId,
    idOffer: seedIdOffer,
  } = location.state || {};

  const { item, isLoading: isLoadingItem } =
    useImprovementClarificationDetail(id);

  const identitySeed = useMemo(
    () => ({
      seedProcurementMitraId:
        seedProcurementMitraId ?? item?.procurementMitraId ?? null,
      seedIdOffer: seedIdOffer ?? item?.idOffer ?? null,
    }),
    [
      seedProcurementMitraId,
      seedIdOffer,
      item?.procurementMitraId,
      item?.idOffer,
    ]
  );

  const {
    procurement,
    isLoading: isLoadingProcurement,
    refresh: refreshProcurement,
  } = useProcurementDetail(id);
  const { parcels, isLoading: isLoadingParcels } = useQuantityDeliveries(id);
  const {
    procurementMitraId: hookMitraId,
    idOffer: hookIdOffer,
    isLoading: isLoadingIdentity,
  } = useOfferMitraIdentity(procurement, identitySeed);
  const { items: submittedBidders } = useSubmittedBidder(id, {
    enabled: Boolean(id),
    pageSize: RESPOND_LIST_PAGE_SIZE,
  });

  const listedMitraIds = useMemo(
    () =>
      (procurement?.procurementMitra ?? [])
        .filter(entry => entry?.id && entry.isDeleted !== true)
        .map(entry => entry.id),
    [procurement]
  );

  const procurementMitraId = useMemo(() => {
    if (identitySeed.seedProcurementMitraId) {
      return identitySeed.seedProcurementMitraId;
    }
    if (submittedBidders.length === 1 && submittedBidders[0]?.id) {
      return submittedBidders[0].id;
    }
    if (listedMitraIds.length === 1) {
      return listedMitraIds[0];
    }
    if (identitySeed.seedIdOffer) {
      const byOffer = submittedBidders.find(
        row => String(row.idOffer) === String(identitySeed.seedIdOffer)
      );
      if (byOffer?.id) return byOffer.id;
    }
    if (hookMitraId) return hookMitraId;
    if (hookIdOffer) {
      const byHookOffer = submittedBidders.find(
        row => String(row.idOffer) === String(hookIdOffer)
      );
      if (byHookOffer?.id) return byHookOffer.id;
    }
    return null;
  }, [
    identitySeed.seedProcurementMitraId,
    identitySeed.seedIdOffer,
    submittedBidders,
    listedMitraIds,
    hookMitraId,
    hookIdOffer,
  ]);

  const idOffer = useMemo(() => {
    if (identitySeed.seedIdOffer) return identitySeed.seedIdOffer;
    if (hookIdOffer) return hookIdOffer;
    const row = submittedBidders.find(
      bidder => String(bidder.id) === String(procurementMitraId)
    );
    return row?.idOffer ?? null;
  }, [
    identitySeed.seedIdOffer,
    hookIdOffer,
    submittedBidders,
    procurementMitraId,
  ]);
  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );

  useEffect(() => {
    setReadSynced(false);
    setLocalClarificationProgress(null);
  }, [id, procurementMitraId]);

  const { hasUnread: hasClarificationUnread, clearUnread } =
    useClarificationHasUnread({
      procurementId: id,
      procurementMitraId,
      clauseDetailIds,
      isEvaluatorView: false,
      enabled: Boolean(id && procurementMitraId && !readSynced),
    });

  useMarkClarificationRespondsAsRead({
    procurementId: id,
    procurementMitraId,
    clauseDetailIds,
    isEvaluatorView: false,
    enabled: Boolean(id && procurementMitraId && activeTab === 2),
    waitForClauseIds: true,
    onComplete: () => {
      clearUnread();
      setReadSynced(true);
    },
  });

  const mitraClarificationProgress = useMemo(() => {
    if (!procurementMitraId) return null;
    const match = submittedBidders.find(
      row => String(row.id) === String(procurementMitraId)
    );
    return match?.statusClarificationProgress ?? null;
  }, [procurementMitraId, submittedBidders]);

  const title = item?.title || procurement?.titleProcurement || '-';
  const procurementNumber =
    item?.idProc || procurement?.procurementNumber || '-';
  const statusImprovementLabel =
    statusImprovementFromState || item?.statusImprovement;
  const statusClarificationLabel =
    item?.statusClarification || statusClarificationFromState;
  const statusClarificationProgress =
    localClarificationProgress ??
    mitraClarificationProgress ??
    statusClarificationProgressFromState ??
    null;
  const isLoading = isLoadingItem || isLoadingProcurement;

  useEffect(() => {
    setVisitedTabs(prev =>
      prev[activeTab] ? prev : { ...prev, [activeTab]: true }
    );
  }, [activeTab]);

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  document.title = 'Improvement Clarification Detail';

  const lastUpdate = item?.updatedDate || item?.createdDate;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <PageBreadcrumb
            fallback={{
              label: 'Improvement Clarification',
              to: '/apps-improvementclarification/view',
            }}
            current={`Detail #${procurementNumber || '-'}`}
            listClassName='p-3 py-2 bg-light'
          />

          <DetailHeader
            loading={isLoading && !item && !procurement}
            onBack={() => navigate('/apps-improvementclarification/view')}
            backLabel='Back to list'
            eyebrow={
              <>
                <i className='ri-hashtag' aria-hidden='true' />
                <span>{procurementNumber}</span>
              </>
            }
            title={title}
            badges={
              <>
                {statusImprovementLabel && (
                  <StatusBadge
                    tone={colorToTone(
                      IMPROVEMENT_STATUS_META[statusImprovementLabel]?.color
                    )}
                    label={`Improvement: ${statusImprovementLabel}`}
                  />
                )}
                {statusClarificationLabel && (
                  <StatusBadge
                    tone={colorToTone(
                      CLARIFICATION_STATUS_META[statusClarificationLabel]?.color
                    )}
                    label={`Clarification: ${statusClarificationLabel}`}
                  />
                )}
              </>
            }
            facts={[
              {
                icon: 'ri-history-line',
                label: 'Last update',
                value: lastUpdate
                  ? dayjs(lastUpdate).format('DD MMM YYYY')
                  : null,
              },
              {
                icon: 'ri-repeat-line',
                label: 'Contract',
                value: getContractTypeLabel(procurement?.contractType),
              },
              {
                icon: 'ri-calendar-2-line',
                label: 'Period',
                value: formatPeriodRange(procurement),
              },
            ]}
          />

          <div className='dt-tabs'>
            <Nav tabs className='nav-tabs-custom nav-primary'>
              <NavItem>
                <NavLink
                  tag='button'
                  type='button'
                  className={classnames('cursor-pointer', {
                    active: activeTab === 1,
                  })}
                  onClick={() => toggleTab(1)}
                >
                  <i className='ri-flashlight-line align-middle me-2'></i>
                  <span className='fw-semibold'>Improvement</span>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag='button'
                  type='button'
                  className={classnames('cursor-pointer', {
                    active: activeTab === 2,
                  })}
                  onClick={() => toggleTab(2)}
                >
                  <i className='ri-question-answer-line align-middle me-2'></i>
                  <span
                    className={
                      hasClarificationUnread
                        ? 'fw-semibold text-info'
                        : 'fw-semibold'
                    }
                  >
                    Clarification
                  </span>
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className='p-3 p-md-4'>
              {visitedTabs[1] && (
                <TabPane tabId={1}>
                  <Improvement
                    procurement={procurement}
                    item={item}
                    procId={id}
                    parcels={parcels}
                    isLoadingParcels={isLoadingParcels}
                    isLoadingProcurement={isLoadingProcurement}
                    idOffer={idOffer}
                    isLoadingIdentity={isLoadingIdentity}
                    procurementMitraId={procurementMitraId}
                  />
                </TabPane>
              )}
              {visitedTabs[2] && (
                <TabPane tabId={2}>
                  <Clarification
                    procurementId={id}
                    procurement={procurement}
                    isLoadingProcurement={isLoadingProcurement}
                    statusClarificationProgress={statusClarificationProgress}
                    onProgressChange={setLocalClarificationProgress}
                    procurementMitraId={procurementMitraId}
                    idOffer={idOffer}
                    isLoadingIdentity={isLoadingIdentity}
                    onRefreshProcurement={refreshProcurement}
                  />
                </TabPane>
              )}
            </TabContent>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DetailPage;
